// The three OT notes, and the merged record.
//
// Surgical, anaesthesia and nursing notes are three separate documents written
// by three different people — so they are three tabs, not one form with a
// "kind" dropdown.
//
// The MERGED view is the operation read as one record (OT_0078/0079). Its
// patient header is assembled by the server from the case, never copied onto the
// notes: every field in it already lives on the case, and a copy would go stale.

import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import {
  IPD_COLORS,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdTabBar,
  IpdTextField,
} from "../../../ipd/utils";
import { formatIstDateTime } from "../../../../utils/istDateTime";
import {
  fetchMergedNotes,
  fetchNote,
  fetchNoteTemplates,
  saveNote,
} from "../../api/otApi";
import { CountResultChip, DisabledHint, Field, FieldGrid, OtSelect, errorMessage, report } from "../common";
import { WOUND_CLASSES, humanise } from "../../constants";
import { printOperativeRecord } from "../../utils/otPdf";

const KINDS = [
  { id: "surgical", label: "Surgical" },
  { id: "anaesthesia", label: "Anaesthesia" },
  { id: "nursing", label: "Nursing" },
  { id: "merged", label: "Merged record" },
];

// Most of a note is prose, because a surgeon's findings are prose. Three
// fields are not — wound class, normothermia and the ASA class recorded at
// clearance — because they have to be COUNTED afterwards: wound class and ASA
// are two thirds of the NHSN surgical-site-infection risk index, and a rate
// that cannot be stratified cannot be compared with anything. The server says
// which fields those are and what values it will accept, so this screen never
// offers one that would be refused.

/** The words for a coded field, where the value alone is not readable. */
const CODED_LABELS = {
  wound_class: WOUND_CLASSES,
};

/**
 * A note value as a reader should see it.
 *
 * Booleans become Yes / No, and a value shaped like a code — all caps, with
 * underscores — becomes words. Deliberately narrow: running every field
 * through `humanise` would also rewrite the surgeon's prose, and the prose is
 * the part that must come back exactly as it was written.
 */
function readable(value) {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const text = String(value);
  return /^[A-Z][A-Z_]*$/.test(text) ? humanise(text.toLowerCase()) : text;
}

/**
 * How each note is laid out.
 *
 * The SERVER decides which fields exist; this only decides the order they are
 * read in and where the headings fall. Anything the server sends that is not
 * listed here still renders — so a field added to the backend appears without a
 * frontend change, which is the property that made the flat list worth keeping.
 *
 * TWO OR THREE SECTIONS, and a `rest` group folded away.
 *
 * Five headings and eighteen boxes is a form people abandon. Watching it in
 * use, they do: the note gets a diagnosis, a line of findings and nothing else,
 * and the fields that were meant to be a record become eighteen blanks that
 * make the note look unfinished when it is not.
 *
 * So each note now opens on the handful of fields its author actually writes,
 * and everything else sits behind one disclosure. Nothing has been removed:
 * a folded field still holds its value, still saves, and the disclosure says
 * how many of them are filled in so a reader can never miss that there is more.
 *
 * What went into `rest` was chosen by asking whether the theatre already knows
 * the answer. The times are stamped by the in-room clock, the nurses' names
 * come from the operating team, the count outcome comes from the count — those
 * do not need typing twice. What stayed is what only the person writing the
 * note can say.
 */
const NOTE_LAYOUT = {
  surgical: [
    {
      title: "OPERATIVE FINDINGS & PROCEDURE DETAILS",
      fields: [
        "pre_op_diagnosis",
        "post_op_diagnosis",
        "procedure_performed",
        "operative_findings",
        "procedure_details",
      ],
    },
    {
      title: "INTRA-OPERATIVE COURSE & WOUND CLASSIFICATION",
      fields: ["wound_class", "estimated_blood_loss", "complications"],
    },
    { title: "POST-OPERATIVE ORDERS & DIRECTIVES", fields: ["post_op_instructions"] },
    {
      rest: true,
      fields: [
        "incision_start_at",
        "procedure_end_at",
        "closure_details",
        "drains",
        "specimens",
        "implants_used",
        "vte_prophylaxis",
      ],
    },
  ],
  anaesthesia: [
    {
      title: "ANESTHETIC TECHNIQUE & AIRWAY",
      fields: ["asa_class", "anaesthesia_technique", "airway_management"],
    },
    {
      title: "INTRA-OPERATIVE FLUIDS & HEMODYNAMICS",
      fields: ["estimated_blood_loss", "fluids_given", "complications"],
    },
    { title: "PACU & POST-ANESTHESIA RECOVERY INSTRUCTIONS", fields: ["recovery_instructions"] },
    {
      rest: true,
      fields: [
        "anaesthesia_start_at",
        "anaesthesia_end_at",
        "blood_products",
        "normothermia_maintained",
        "lowest_temperature",
        "anaesthetist_name",
        "assistant_name",
        "notes",
      ],
    },
  ],
  nursing: [
    {
      title: "SURGICAL POSITIONING & ASEPTIC PREPARATION",
      fields: ["positioning", "skin_preparation", "skin_condition"],
    },
    {
      title: "SURGICAL SPECIMENS & TISSUE DISPATCH",
      fields: ["specimens_sent", "implants_used"],
    },
    {
      rest: true,
      fields: [
        "scrub_nurse_name",
        "circulating_nurse_name",
        "diathermy_site",
        "tourniquet_details",
        "disposals",
        "notes",
      ],
    },
  ],
};

/**
 * Group the server's field list for display, keeping anything unrecognised.
 *
 * A field the layout does not mention joins the folded group rather than
 * appearing on its own at the bottom: a new backend field showing up unheralded
 * in the middle of somebody's note is how a form grows back to eighteen boxes.
 */
function layoutFor(kind, fields, required = []) {
  const groups = NOTE_LAYOUT[kind] || [];
  const placed = new Set();
  const laid = groups
    .map((group) => {
      const present = group.fields.filter((f) => fields.includes(f));
      present.forEach((f) => placed.add(f));
      return { ...group, fields: present };
    })
    .filter((group) => group.fields.length > 0);

  // A field that blocks the close is never folded away. The layout already puts
  // the core up front for every kind we ship; this holds if the server's
  // required list ever grows past what NOTE_LAYOUT knows about.
  const requiredSet = new Set(required);
  const strandedRequired = fields.filter((f) => requiredSet.has(f) && !placed.has(f));
  if (strandedRequired.length) {
    strandedRequired.forEach((f) => placed.add(f));
    laid.unshift({ title: "REQUIRED", fields: strandedRequired });
  }

  const leftover = fields.filter((f) => !placed.has(f));
  if (!leftover.length) return laid;

  const rest = laid.find((g) => g.rest);
  if (rest) {
    rest.fields = [...rest.fields, ...leftover];
    return laid;
  }
  return [...laid, { rest: true, fields: leftover }];
}

/** Fields that read better as a paragraph than a single line. */
const LONG_FIELDS = new Set([
  "operative_findings",
  "procedure_details",
  "complications",
  "closure_details",
  "post_op_instructions",
  "specimens",
  "implants_used",
  "recovery_instructions",
  "notes",
  "disposals",
  "positioning",
  "skin_preparation",
  "specimens_sent",
  "fluids_given",
  "blood_products",
  "pre_op_diagnosis",
  "post_op_diagnosis",
  "procedure_performed",
]);

export default function NotesPanel({ otCase, onChanged }) {
  const [kind, setKind] = useState("surgical");

  return (
    <Box>
      <IpdTabBar
        tabs={KINDS}
        activeTab={kind}
        onChange={setKind}
        ariaLabel="Which operation note"
      />

      {kind === "merged" ? (
        <MergedRecord otCase={otCase} />
      ) : (
        <NoteEditor key={kind} otCase={otCase} kind={kind} onChanged={onChanged} />
      )}
    </Box>
  );
}

export function NoteEditor({ otCase, kind, onChanged }) {
  const [payload, setPayload] = useState(null);
  const [draft, setDraft] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState("");
  const [showRest, setShowRest] = useState(false);

  // Folded again whenever the author changes, so opening the anaesthesia note
  // does not inherit the surgeon having unfolded theirs.
  useEffect(() => {
    setShowRest(false);
  }, [kind]);

  useEffect(() => {
    fetchNoteTemplates({ note_kind: kind })
      .then((rows) => setTemplates(rows || []))
      .catch(() => setTemplates([]));
  }, [kind]);

  /**
   * Apply a template over the note.
   *
   * Fills EMPTY fields only, and asks before touching anything already
   * typed. A surgeon halfway through an operative note should not lose it
   * to a mis-click on a dropdown.
   */
  const applyTemplate = () => {
    const chosen = templates.find((t) => String(t.id) === String(template));
    if (!chosen) return;
    const clashes = Object.keys(chosen.fields || {}).filter(
      (f) => String(draft[f] ?? "").trim()
    );
    if (clashes.length) {
      const ok = window.confirm(
        `${clashes.length} field(s) already have text in them:

` +
          clashes.map(humanise).join(", ") +
          `

Replace what is there with the template?`
      );
      if (!ok) {
        // Fill only the gaps.
        setDraft((d) => {
          const next = { ...d };
          Object.entries(chosen.fields || {}).forEach(([f, v]) => {
            if (!String(next[f] ?? "").trim()) next[f] = v;
          });
          return next;
        });
        return;
      }
    }
    setDraft((d) => ({ ...d, ...chosen.fields }));
  };

  const load = useCallback(async () => {
    setError("");
    try {
      const body = await fetchNote(otCase.id, kind);
      setPayload(body);
      setDraft(body.note || {});
    } catch (err) {
      setError(errorMessage(err, "Could not load the note"));
    }
  }, [otCase.id, kind]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (extra) => {
    setSaving(true);
    try {
      const body = {};
      (payload?.fields || []).forEach((f) => {
        if (draft[f] !== undefined) body[f] = draft[f];
      });
      if (report(await saveNote(otCase.id, kind, { ...body, ...extra }))) {
        await load();
        onChanged();
      }
    } finally {
      setSaving(false);
    }
  };

  if (error) return <Typography sx={{ color: IPD_COLORS.error.text }}>{error}</Typography>;
  if (!payload) return null;

  const note = payload.note;
  const fields = payload.fields || [];
  const coded = payload.coded_fields || {};
  const flags = payload.flag_fields || [];
  // The server says which fields the close gate will demand, so the asterisks
  // and the refusal can never disagree.
  const required = payload.required_fields || [];

  const groups = layoutFor(kind, fields, required);
  const restFields = groups.find((g) => g.rest)?.fields || [];
  const restFilled = restFields.filter(
    (f) => draft[f] !== undefined && draft[f] !== null && String(draft[f]).trim() !== ""
  ).length;

  return (
    <IpdSectionCard
      title={`${humanise(kind)} note`}
      subtitle={
        note?.certified
          ? `Certified by ${note.certified_by_name} on ${formatIstDateTime(note.certified_at)} — later corrections do not move that stamp.`
          : "Not yet certified."
      }
      actions={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {templates.length > 0 && (
            <>
              <Box sx={{ width: 190 }}>
                <OtSelect
                  placeholder="Template"
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  options={templates.map((t) => ({ value: t.id, label: t.name }))}
                />
              </Box>
              <Button size="small" disabled={!template} onClick={applyTemplate}>
                Apply
              </Button>
            </>
          )}
          <Button size="small" disabled={saving} onClick={() => save()}>
            Save
          </Button>
          <DisabledHint
            reason={
              !saving &&
              note?.certified &&
              "This operative note has already been certified and cannot be certified again."
            }
          >
            <IpdButton disabled={saving || note?.certified} onClick={() => save({ certify: true })}>
              Save &amp; certify
            </IpdButton>
          </DisabledHint>
        </Box>
      }
    >
      {kind === "nursing" && note?.count_result && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 13 }}>Count outcome:</Typography>
          <CountResultChip result={note.count_result} />
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
            from the count
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "grid", gap: 2.5 }}>
        {groups.map((group) => (
          <Box
            key={group.title || "rest"}
            // The folded group renders its fields either way, so a value in one
            // is still in the draft and still saved — it is only out of sight.
            sx={{ display: group.rest && !showRest ? "none" : "block" }}
          >
            {group.rest && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 1,
                  mb: 1,
                  pt: 1,
                  borderTop: `1px solid ${IPD_COLORS.border}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 800,
                    letterSpacing: "0.04em",
                    color: IPD_COLORS.textSecondary,
                  }}
                >
                  ADDITIONAL PERIOPERATIVE CLINICAL PARAMETERS
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                  Verify automated theatre timestamps, clinical staff attributions, and supplementary operative details.
                </Typography>
              </Box>
            )}
            {group.title && (
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  color: IPD_COLORS.textSecondary,
                  mb: 1,
                }}
              >
                {group.title}
              </Typography>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              {group.fields.map((f) => {
                const long = LONG_FIELDS.has(f);
                const isTime = f.endsWith("_at");
                const options = coded[f];
                const isFlag = flags.includes(f);
                return (
                  <Box key={f} sx={{ gridColumn: long ? { xs: "span 1", md: "span 2" } : "span 1" }}>
                    <IpdFormField label={humanise(f)} required={required.includes(f)}>
                      {options ? (
                        <OtSelect
                          value={draft[f] ?? ""}
                          onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))}
                          // The server sends the values; the labels are ours. A value
                          // it sends that we have no words for still renders, as
                          // itself, rather than vanishing from the dropdown.
                          options={options.map((value) => ({
                            value,
                            label:
                              (CODED_LABELS[f] || []).find((o) => o.value === value)?.label ||
                              humanise(value),
                          }))}
                          placeholder="Not recorded"
                        />
                      ) : isFlag ? (
                        // Three states, not two: nobody has answered yet is a
                        // different fact from no, and a checkbox cannot say it.
                        <OtSelect
                          value={
                            draft[f] === true || draft[f] === "true"
                              ? "true"
                              : draft[f] === false || draft[f] === "false"
                              ? "false"
                              : ""
                          }
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              [f]: e.target.value === "" ? "" : e.target.value === "true",
                            }))
                          }
                          options={[
                            { value: "true", label: "Yes" },
                            { value: "false", label: "No" },
                          ]}
                          placeholder="Not recorded"
                        />
                      ) : (
                        <IpdTextField
                          fullWidth
                          multiline={long}
                          minRows={long ? 3 : undefined}
                          type={isTime ? "datetime-local" : "text"}
                          InputLabelProps={isTime ? { shrink: true } : undefined}
                          value={draft[f] ?? ""}
                          onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))}
                        />
                      )}
                    </IpdFormField>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}

        {restFields.length > 0 && (
          <Box>
            <Button size="small" onClick={() => setShowRest((v) => !v)} sx={{ px: 0 }}>
              {showRest
                ? "Collapse Supplementary Parameters"
                : `Show ${restFields.length} Supplementary Clinical Parameter${restFields.length === 1 ? "" : "s"}`}
            </Button>
            {/* Said out loud, because a folded field with something in it is the
                one thing hiding fields could genuinely lose. */}
            {!showRest && restFilled > 0 && (
              <Typography component="span" sx={{ ml: 1, fontSize: 12, color: IPD_COLORS.textMuted }}>
                ({restFilled} parameters populated)
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </IpdSectionCard>
  );
}

function MergedRecord({ otCase }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchMergedNotes(otCase.id)
      .then(setData)
      .catch((err) => setError(errorMessage(err, "Could not assemble the record")));
  }, [otCase.id]);

  if (error) return <Typography sx={{ color: IPD_COLORS.error.text }}>{error}</Typography>;
  if (!data) return null;

  const h = data.header || {};

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <IpdSectionCard
        title="Surgical Episode Summary"
        actions={
          <Button size="small" onClick={() => printOperativeRecord(data)}>
            Print Operative Record
          </Button>
        }
      >
        <FieldGrid columns={4}>
          <Field label="Case" value={h.case_number} />
          <Field label="Patient" value={h.patient_name} />
          <Field label="MRN" value={h.health_record_id} />
          <Field label="Age / Sex" value={`${h.age ?? "—"} / ${h.gender || "—"}`} />
          <Field label="Ward" value={h.ward_name} />
          <Field label="Theatre" value={h.place} />
          <Field label="Case type" value={h.case_type} />
          <Field label="Date" value={formatIstDateTime(h.date)} />
          <Field label="Procedure" value={h.primary_procedure} span={2} />
          <Field label="Surgeon" value={h.primary_surgeon_name} />
          <Field label="Anaesthetist" value={h.anaesthetist_name} />
          <Field label="Wheeled In" value={formatIstDateTime(h.in_or_at)} />
          <Field label="Wheeled Out" value={formatIstDateTime(h.out_of_or_at)} />
        </FieldGrid>

        {(data.team || []).length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.5 }}>Surgical Team Roster</Typography>
            {data.team.map((m) => (
              <Typography key={m.id} sx={{ fontSize: 13 }}>
                {m.attendee_type_name}: {m.member_name}
                {m.grade ? ` (${m.grade})` : ""}
              </Typography>
            ))}
          </Box>
        )}
      </IpdSectionCard>

      {["surgical", "anaesthesia", "nursing"].map((k) => {
        const note = data[k];
        if (!note) return null;
        return (
          <IpdSectionCard key={k} title={`${humanise(k)} Clinical Record`}>
            <FieldGrid columns={2}>
              {Object.keys(note)
                .filter(
                  (key) =>
                    !["id", "kind", "certified", "certified_at", "updated_at"].includes(key) &&
                    note[key] !== null &&
                    note[key] !== ""
                )
                .map((key) => (
                  <Field
                    key={key}
                    label={humanise(key)}
                    value={
                      key.endsWith("_at")
                        ? formatIstDateTime(note[key])
                        : readable(note[key])
                    }
                    span={LONG_FIELDS.has(key) ? 2 : 1}
                  />
                ))}
            </FieldGrid>
          </IpdSectionCard>
        );
      })}

      {(data.counts || []).length > 0 && (
        <IpdSectionCard title="Surgical Counts Reconciliation">
          {data.counts.map((c) => (
            <Box key={c.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.75 }}>
              <Typography sx={{ fontSize: 13, flex: 1 }}>
                {c.item_name} — expected {c.expected_at_closure}, counted{" "}
                {c.closure_count ?? "not taken"}
              </Typography>
              <CountResultChip result={c.result} />
            </Box>
          ))}
        </IpdSectionCard>
      )}

      {(data.consumption || []).length > 0 && (
        <IpdSectionCard title="Material & Consumable Consumption Record">
          {data.consumption.map((c) => (
            <Typography key={c.id} sx={{ fontSize: 13, py: 0.4 }}>
              {c.item_name} × {c.quantity} {c.unit || ""} ({c.item_type})
              {c.batch_no ? ` · batch ${c.batch_no}` : ""}
            </Typography>
          ))}
        </IpdSectionCard>
      )}
    </Box>
  );
}
